import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { aiService } from '../../services/aiService';

export default function FeedScan({ feedRunning, setFeedRunning }) {
  const { partners, addIdea, anthropicKey, anthropicKeyReady, currentUser, logActivity } = useApp();
  const [results, setResults] = useState([]);
  const [error,   setError]   = useState('');

  const monitoredPartners = partners.filter(p => p.monitor);

  async function runFeedScan() {
    if (!anthropicKeyReady) { setError('Add your Anthropic key in Settings → API Keys first.'); return; }
    if (monitoredPartners.length === 0) { setError('Add monitored partners in Settings → Partners first.'); return; }
    setFeedRunning(true);
    setError('');
    setResults([]);

    try {
      const partnerList = monitoredPartners.map(p => p.name).join(', ');
      const prompt = `You are helping Tayst Coffee monitor partner activity on LinkedIn.

Partners being monitored: ${partnerList}

Simulate what might be on their feeds today (${new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}).

Return a JSON array of 4-6 hypothetical LinkedIn post ideas we could respond to or create content around:
[{
  "partner": "partner name",
  "post_summary": "brief description of what they posted",
  "content_idea": "how Tayst could respond or create related content",
  "urgency": "hot|soon|anytime"
}]

Return only the JSON array, no other text.`;

      const text = await aiService.rawComplete({ message: prompt, maxTokens: 1024, anthropicKey });
      const match = text.replace(/```json|```/g,'').trim().match(/\[[\s\S]*\]/);
      const items = match ? JSON.parse(match[0]) : [];
      setResults(items);
    } catch (err) {
      setError('Feed scan failed: ' + err.message);
    } finally {
      setFeedRunning(false);
    }
  }

  async function promoteToIdea(item) {
    try {
      await addIdea({
        source:   'feed',
        title:    item.content_idea,
        notes:    `From partner feed: ${item.partner} — "${item.post_summary}"`,
        category: 'partner',
        urgency:  item.urgency || 'soon',
        status:   'new',
        world:    'business',
      });
      logActivity('Promoted feed item to idea', item.content_idea);
      setResults(prev => prev.filter(r => r !== item));
    } catch (err) {
      alert('Failed to add idea: ' + err.message);
    }
  }

  return (
    <div style={{ padding:'0 0 20px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700 }}>LinkedIn Feed Scan</div>
          <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
            {monitoredPartners.length} partner{monitoredPartners.length !== 1 ? 's' : ''} monitored
          </div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={runFeedScan} disabled={feedRunning}>
          {feedRunning ? <>Scanning <span className="dots"><i/><i/><i/></span></> : '📡 Scan Feed'}
        </button>
      </div>

      {monitoredPartners.length === 0 && (
        <div className="info-box amber">
          No partners set to monitor. Add hotel / partner companies in Settings → Partners and enable monitoring.
        </div>
      )}

      {error && <div className="err-bar" style={{ marginBottom:12 }}>{error}</div>}

      {results.length > 0 && (
        <div style={{ display:'grid', gap:10 }}>
          {results.map((item, i) => (
            <div key={i} style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:14, boxShadow:'var(--sh)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--sky)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--sky-dk)' }}>
                  {item.partner.slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700 }}>{item.partner}</div>
                  <div style={{ fontSize:10, color:'#9ca3af' }}>LinkedIn post</div>
                </div>
              </div>
              <div style={{ fontSize:11.5, color:'#4b5563', marginBottom:8, lineHeight:1.5 }}>
                {item.post_summary}
              </div>
              <div style={{ background:'var(--caramel-ll)', borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:11.5 }}>
                <span style={{ fontWeight:700, color:'var(--caramel)' }}>Content idea: </span>
                {item.content_idea}
              </div>
              <button className="btn btn-primary btn-xs" onClick={() => promoteToIdea(item)}>
                + Promote to Idea Lab
              </button>
            </div>
          ))}
        </div>
      )}

      {!feedRunning && results.length === 0 && !error && (
        <div className="empty-state" style={{ paddingTop:30 }}>
          <div style={{ fontSize:28, marginBottom:8 }}>📡</div>
          Click "Scan Feed" to check what your partners are posting and get content ideas.
        </div>
      )}
    </div>
  );
}
